import { useDocumentFormattingDiagnostics } from "./useDocumentFormattingDiagnostics";
import { useDocumentMechanicsDiagnostics } from "./useDocumentMechanicsDiagnostics";
import { useDocumentParagraphDiagnostics } from "./useDocumentParagraphDiagnostics";
import { useDocumentStyleDiagnostics } from "./useDocumentStyleDiagnostics";

export const useDocumentDiagnostics = (documentId: string) =>
  defineStore(`/document/${documentId}/diagnostics`, () => {
    const diagnosticsConfiguration = ref({
      isEnabled: true,
      enableBodyParagraphDiagnostics: true,
      formattingDiagnostics: {
        isEnabled: true,
        label: "Checking document formatting.",
        showDiagnostics: true,
        diagnostics: [
          {
            label: "Title",
            metricId: "Rj9LkcOjuJ8Vuo29djXi",
            defaultErrorMessage: "The title is missing or not centered.",
            isEvaluationRunning: false,
            type: "paragraph",
            alignment: "CENTER",
            errorCodeMap: {
              "no-relevant-instances": "No title found.",
              "incorrect-alignment": "The title is not centered.",
            },
          },
        ],
      },
      styleDiagnostics: {
        isEnabled: true,
        label: "Checking style.",
        showDiagnostics: true,
        diagnostics: [
          {
            label: "-ly adverbs",
            metricId: "LBslHxxxIaRNBwVfa3C8",
            defaultErrorMessage:
              "The document does not contain any -ly adverbs.",
            isEvaluationRunning: false,
            errorCodeMap: {
              "no-relevant-instances": "No -ly adverbs found.",
              "not-enough-instances": "Not enough -ly adverbs found.",
            },
            expectedInBodyParagraphs: [1, 2, 3],
          },
          {
            label: "who/which clause",
            metricId: "dIWr3FMs5f2e93dmGyqk",
            defaultErrorMessage:
              "The document does not contain any who/which clauses.",
            errorCodeMap: {
              "no-relevant-instances": "No who/which clauses found.",
              "not-enough-instances": "Not enough who/which clauses found.",
            },
            expectedInBodyParagraphs: [1, 2, 3],
          },
        ],
      },
      metaDataDiagnostics: {
        isEnabled: true,
        label: "Checking mechanics.",
        showDiagnostics: true,
        diagnostics: [
          {
            label: "Spelling mistakes",
            metricId: "zF6q0a4GRJvNJlcCbJiI",
            defaultErrorMessage: "The document contains spelling mistakes.",
            isEvaluationRunning: false,
            errorCodeMap: {
              "has-relevant-instances":
                "The document contains spelling mistakes.",
            },
          },
        ],
      },
    });

    const diagnosticsMap = ref<{
      [key: string]: {
        label: string;
        isRunning: any;
        hasPassedDiagnostics: any;
        diagnosticsErrorMessage: any;
        diagnostics?: any[];
      };
    }>({});

    const paragraphDiagnostics = useDocumentParagraphDiagnostics(documentId)();
    const {
      diagnosticsLabel: paragraphDiagnosticsLabel,
      isRunning: paragraphDiagnosticsIsRunning,
      hasPassedDiagnostics: paragraphDiagnosticsHasPassedDiagnostics,
      diagnosticsErrorMessage: paragraphDiagnosticsDiagnosticsErrorMessage,
    } = storeToRefs(paragraphDiagnostics);

    const formattingDiagnostics =
      useDocumentFormattingDiagnostics(documentId)();
    const {
      isRunning: formattingDiagnosticsIsRunning,
      hasPassedDiagnostics: formattingDiagnosticsHasPassedDiagnostics,
      diagnosticsErrorMessage: formattingDiagnosticsDiagnosticsErrorMessage,
    } = storeToRefs(formattingDiagnostics);

    const styleDiagnostics = useDocumentStyleDiagnostics(documentId)();
    const {
      isRunning: styleDiagnosticsIsRunning,
      hasPassedDiagnostics: styleDiagnosticsHasPassedDiagnostics,
      diagnosticsErrorMessage: styleDiagnosticsDiagnosticsErrorMessage,
    } = storeToRefs(styleDiagnostics);

    const mechanicsDiagnostics = useDocumentMechanicsDiagnostics(documentId)();
    const {
      isRunning: mechanicsDiagnosticsIsRunning,
      hasPassedDiagnostics: mechanicsDiagnosticsHasPassedDiagnostics,
      diagnosticsErrorMessage: mechanicsDiagnosticsDiagnosticsErrorMessage,
    } = storeToRefs(mechanicsDiagnostics);

    const runParagraphDiagnostics = async () => {
      if (diagnosticsConfiguration.value.enableBodyParagraphDiagnostics) {
        diagnosticsMap.value["paragraphs"] = {
          label: paragraphDiagnosticsLabel.value,
          isRunning: paragraphDiagnosticsIsRunning,
          hasPassedDiagnostics: paragraphDiagnosticsHasPassedDiagnostics,
          diagnosticsErrorMessage: paragraphDiagnosticsDiagnosticsErrorMessage,
        };

        await paragraphDiagnostics.runDiagnostics();
      }
    };

    const runDiagnostics = async () => {
      if (diagnosticsConfiguration.value.enableBodyParagraphDiagnostics) {
        diagnosticsMap.value["paragraphs"] = {
          label: paragraphDiagnosticsLabel.value,
          isRunning: paragraphDiagnosticsIsRunning,
          hasPassedDiagnostics: paragraphDiagnosticsHasPassedDiagnostics,
          diagnosticsErrorMessage: paragraphDiagnosticsDiagnosticsErrorMessage,
        };

        await paragraphDiagnostics.runDiagnostics();
      }

      // if (diagnosticsConfiguration.value.formattingDiagnostics.isEnabled) {
      //   diagnosticsMap.value["formatting"] = {
      //     label: diagnosticsConfiguration.value.formattingDiagnostics.label,
      //     isRunning: formattingDiagnosticsIsRunning,
      //     hasPassedDiagnostics: formattingDiagnosticsHasPassedDiagnostics,
      //     diagnosticsErrorMessage: formattingDiagnosticsDiagnosticsErrorMessage,
      //     diagnostics:
      //       diagnosticsConfiguration.value.formattingDiagnostics.diagnostics,
      //   };

      //   await formattingDiagnostics.runDiagnostics(
      //     diagnosticsConfiguration.value.formattingDiagnostics
      //   );
      // }

      // if (diagnosticsConfiguration.value.styleDiagnostics.isEnabled) {
      //   diagnosticsMap.value["style"] = {
      //     label: diagnosticsConfiguration.value.styleDiagnostics.label,
      //     isRunning: styleDiagnosticsIsRunning,
      //     hasPassedDiagnostics: styleDiagnosticsHasPassedDiagnostics,
      //     diagnosticsErrorMessage: styleDiagnosticsDiagnosticsErrorMessage,
      //     diagnostics:
      //       diagnosticsConfiguration.value.styleDiagnostics.diagnostics,
      //   };

      //   await styleDiagnostics.runDiagnostics(
      //     diagnosticsConfiguration.value.styleDiagnostics
      //   );
      // }

      // if (diagnosticsConfiguration.value.metaDataDiagnostics.isEnabled) {
      //   diagnosticsMap.value["metaData"] = {
      //     label: diagnosticsConfiguration.value.metaDataDiagnostics.label,
      //     isRunning: mechanicsDiagnosticsIsRunning,
      //     hasPassedDiagnostics: mechanicsDiagnosticsHasPassedDiagnostics,
      //     diagnosticsErrorMessage: mechanicsDiagnosticsDiagnosticsErrorMessage,
      //     diagnostics:
      //       diagnosticsConfiguration.value.metaDataDiagnostics.diagnostics,
      //   };

      //   await mechanicsDiagnostics.runDiagnostics(
      //     diagnosticsConfiguration.value.metaDataDiagnostics
      //   );
      // }
    };

    return {
      runDiagnostics,
      runParagraphDiagnostics,
      diagnosticsMap,
      diagnosticsConfiguration,
    };
  });
